import { AlertCircle, ChevronRight, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { assistantService } from '../services/assistant.service';
import type { LigneResultat } from '../types/assistant.types';

/**
 * Ce qu'une lecture a trouvé : des lignes fines, toute la ligne cliquable
 * quand elle mène quelque part (même code que le journal des envois).
 * L'état normal ne porte aucune pastille ; seule l'exception est signalée.
 */
export function ListeResultats({ lignes }: { lignes: LigneResultat[] }) {
  const [enCours, setEnCours] = useState<Set<string>>(new Set());
  return (
    <div className="mt-2 max-w-xl rounded-xl border border-neutral-200 bg-white p-2 shadow-sm">
      {lignes.map(ligne => {
        const contenu = (
          <>
            {ligne.alerte ? (
              <AlertCircle className="text-warning-600 mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <span className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            {/* Le motif d'un échec est ce qu'on est venu lire : en entier,
                sans infobulle (le survol n'existe pas au doigt). */}
            <span className="min-w-0 flex-1 break-words">
              <span className="font-medium text-neutral-900">
                {ligne.libelle}
              </span>
              {ligne.detail && (
                <span className="text-neutral-500"> · {ligne.detail}</span>
              )}
            </span>
            {ligne.lien && (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
            )}
            {ligne.telechargement &&
              (enCours.has(ligne.id) ? (
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-neutral-400" />
              ) : (
                <Download className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
              ))}
          </>
        );
        const classes =
          'flex min-h-8 w-full items-start gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-neutral-50 disabled:opacity-60';
        if (ligne.telechargement) {
          const { chemin, nomFichier } = ligne.telechargement;
          return (
            <button
              key={ligne.id}
              type="button"
              className={classes}
              disabled={enCours.has(ligne.id)}
              onClick={() => {
                if (enCours.has(ligne.id)) return;
                setEnCours(prev => new Set(prev).add(ligne.id));
                void assistantService
                  .telecharger(chemin, nomFichier)
                  .catch(() =>
                    toast.error(
                      'Ce bulletin n’est pas disponible : vérifiez qu’il est bien généré dans OpenPaye.'
                    )
                  )
                  .finally(() =>
                    setEnCours(prev => {
                      const suivant = new Set(prev);
                      suivant.delete(ligne.id);
                      return suivant;
                    })
                  );
              }}
            >
              {contenu}
            </button>
          );
        }
        return ligne.lien ? (
          <Link key={ligne.id} to={ligne.lien} className={classes}>
            {contenu}
          </Link>
        ) : (
          <div key={ligne.id} className={classes}>
            {contenu}
          </div>
        );
      })}
    </div>
  );
}
